/**
 * Catálogo — serve tanto Agentes quanto Sistemas (AIH-37).
 *
 * As duas seções têm a mesma anatomia: banner, contagens, abas por área, busca
 * e grade. Muda o recorte e o texto. Um componente com `tipo` em vez de dois
 * arquivos quase iguais — porque duplicar aqui significa corrigir tudo duas
 * vezes, e uma delas vai ser esquecida.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { AppShell, Banner, StatsBar } from '../components/AppShell.jsx';
import { ItemCard } from '../components/ItemCard.jsx';
import { ItemPanel } from '../components/ItemPanel.jsx';
import { Toast } from '../components/Toast.jsx';
import { api, SessionError, goToSignIn } from '../api.js';
import { onAccessChanged } from '../accessEvents.js';
import { AREAS_INICIAIS, TODAS, carregarAreas } from '../areas.js';

const COPY = {
  AGENT: {
    title: 'Agentes',
    banner: (name) => (name ? `Bem-vindo de volta, ${name}!` : 'Agentes de IA'),
    search: 'Buscar agente…',
    empty: 'Nenhum agente encontrado.',
    countLabel: 'Agentes disponíveis',
  },
  SYSTEM: {
    title: 'Sistemas',
    banner: () => 'Sistemas & Plataformas',
    search: 'Buscar sistema…',
    empty: 'Nenhum sistema encontrado.',
    countLabel: 'Sistemas disponíveis',
  },
};

export function Catalog({ kind = 'AGENT' }) {
  const t = COPY[kind];
  usePageMeta(`AI Hub — ${t.title.toLowerCase()}`);

  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(TODAS);
  // As abas vêm do servidor desde 25/10. A lista de partida existe só para
  // a barra não nascer vazia e pular quando a resposta chegar.
  const [areas, setAreas] = useState(AREAS_INICIAIS);
  useEffect(() => {
    let vivo = true;
    carregarAreas().then((a) => vivo && setAreas(a));
    return () => {
      vivo = false;
    };
  }, []);

  const [search, setSearch] = useState('');
  const [openItem, setOpenItem] = useState(null);

  /**
   * @param {object} [o]
   * @param {boolean} [o.silent] sem "Carregando…" — para a recarga automática,
   *   que não pode fazer a tela piscar debaixo de quem está lendo.
   */
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      // Em paralelo: nenhuma depende da outra, e em série seriam duas esperas
      // de rede somadas antes de a tela desenhar.
      const [catalog, profile] = await Promise.all([api.catalog(), api.me()]);
      setData(catalog);
      setMe(profile);

      // O painel aberto guarda uma cópia do item de antes da recarga. Sem
      // trocá-la, ele continua dizendo "Você ainda não tem acesso" enquanto o
      // cartão atrás já mostra "Liberado" — que foi exatamente o sintoma.
      setOpenItem((atual) =>
        atual ? (catalog.items.find((i) => i.id === atual.id) ?? atual) : atual,
      );
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * O sino avisa quando um acesso muda; aqui a tela se refaz.
   *
   * É o que fecha o ciclo: a liberação acontece no servidor, o sino a
   * descobre, e o portfólio para de mentir sem ninguém apertar F5.
   */
  useEffect(() => onAccessChanged(() => load({ silent: true })), [load]);

  // Trocar de seção zera o recorte: filtro de Agentes aplicado a Sistemas
  // mostraria "nenhum resultado" sem a pessoa entender por quê.
  useEffect(() => {
    setTab(TODAS);
    setSearch('');
  }, [kind]);

  const ofKind = useMemo(() => (data?.items ?? []).filter((i) => i.kind === kind), [data, kind]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ofKind.filter((i) => {
      // `Todos` na coluna de áreas é coringa: o item aparece em qualquer aba.
      const inArea = tab === TODAS || i.areas.includes(TODAS) || i.areas.includes(tab);
      if (!inArea) return false;
      if (!term) return true;
      return (
        i.name.toLowerCase().includes(term) ||
        (i.description ?? '').toLowerCase().includes(term) ||
        (i.tags ?? []).some((g) => g.toLowerCase().includes(term))
      );
    });
  }, [ofKind, tab, search]);

  const openable = ofKind.filter((i) => i.access.canOpen).length;

  return (
    <AppShell>
      <Banner title={t.banner(me?.firstName)} />

      <StatsBar
        items={[
          { label: t.countLabel, value: ofKind.length },
          { label: 'Que você já pode abrir', value: openable },
        ]}
      />

      {data?.groupListIncomplete && (
        <div className="notice-bar" role="status">
          <strong>Não foi possível confirmar todas as suas permissões.</strong> A lista pode estar
          incompleta — tente abrir antes de solicitar.
        </div>
      )}

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs" role="tablist" aria-label="Filtrar por área">
            {[TODAS, ...areas].map((a) => (
              <button
                key={a}
                type="button"
                role="tab"
                aria-selected={tab === a}
                className={`tab ${tab === a ? 'is-active' : ''}`}
                onClick={() => setTab(a)}
              >
                {a}
              </button>
            ))}
          </div>

          <input
            className="search-box"
            type="search"
            placeholder={t.search}
            aria-label={t.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="grid">
          {loading && <p className="state-message">Carregando…</p>}

          {error && (
            <div className="state-message error" role="alert">
              <p>{error}</p>
              <button className="button secondary" type="button" onClick={load}>
                Tentar de novo
              </button>
            </div>
          )}

          {!loading && !error && visible.length === 0 && (
            <p className="state-message">
              {ofKind.length === 0 ? t.empty : 'Nada corresponde ao que você buscou.'}
            </p>
          )}

          {!loading &&
            !error &&
            visible.map((item, i) => (
              <ItemCard key={item.id} item={item} onOpen={setOpenItem} primeiro={i === 0} />
            ))}
        </div>
      </div>

      {openItem && (
        <ItemPanel
          item={openItem}
          onClose={() => setOpenItem(null)}
          onSubmitted={load}
          isAdmin={Boolean(me?.isAdmin)}
        />
      )}

      <Toast />
    </AppShell>
  );
}
